import React from 'react';
import { Minus, TrendingDown, TrendingUp } from 'lucide-react';
import { SalesPerformance } from '../../types';

type Rating = 'good' | 'moderate' | 'poor';

interface VisitSalesPerformanceSectionProps {
  performance: SalesPerformance;
  resolveProductName: (productId: string) => string;
}

const ratingIcon = (rating: Rating) => {
  switch (rating) {
    case 'good':
      return <TrendingUp size={16} className="text-green-600" />;
    case 'poor':
      return <TrendingDown size={16} className="text-red-600" />;
    default:
      return <Minus size={16} className="text-yellow-600" />;
  }
};

const ratingCardColor = (rating: Rating) => {
  switch (rating) {
    case 'good':
      return 'border-green-200 bg-green-50';
    case 'poor':
      return 'border-red-200 bg-red-50';
    default:
      return 'border-yellow-200 bg-yellow-50';
  }
};

const ratingTextColor = (rating: Rating) => {
  switch (rating) {
    case 'good':
      return 'text-green-700';
    case 'poor':
      return 'text-red-700';
    default:
      return 'text-yellow-700';
  }
};

const ratingLabel = (rating: Rating) => {
  switch (rating) {
    case 'good':
      return 'Gut (läuft sehr gut)';
    case 'poor':
      return 'Schlecht (läuft nicht)';
    default:
      return 'Mittel (durchschnittlich)';
  }
};

const Metric: React.FC<{ label: string; value: number }> = ({ label, value }) => (
  <div className="bg-white p-2 rounded border border-gray-200">
    <p className="text-xs text-gray-600 uppercase tracking-wide">{label}</p>
    <p className="text-xl font-black text-gray-900">{value}</p>
  </div>
);

export const VisitSalesPerformanceSection: React.FC<VisitSalesPerformanceSectionProps> = ({
  performance,
  resolveProductName,
}) => {
  const entries = Object.entries(performance);
  if (entries.length === 0) return null;

  return (
    <div className="bg-purple-50 p-4 rounded-xl border border-purple-200">
      <div className="flex items-center gap-2 mb-3">
        <TrendingUp size={18} className="text-purple-600" />
        <h3 className="font-bold text-purple-900">Verkaufsperformance</h3>
      </div>
      <div className="space-y-3">
        {entries.map(([productId, data]) => (
          <div key={productId} className={`p-4 border-2 rounded-lg ${ratingCardColor(data.rating)}`}>
            <div className="flex items-center gap-2 mb-3">
              {ratingIcon(data.rating)}
              <h4 className="font-bold text-gray-900">{resolveProductName(productId)}</h4>
            </div>
            <div className="grid grid-cols-2 gap-3 text-sm">
              <Metric label="Verkaufte Einheiten" value={data.units_sold} />
              <Metric label="Aktueller Bestand" value={data.current_stock} />
            </div>
            <div className="mt-2 bg-white p-2 rounded border border-gray-200">
              <p className="text-xs text-gray-600 uppercase tracking-wide mb-1">Bewertung</p>
              <p className={`font-bold ${ratingTextColor(data.rating)}`}>
                {ratingLabel(data.rating)}
              </p>
            </div>
            {data.notes && (
              <div className="mt-2 bg-white p-2 rounded border border-gray-200">
                <p className="text-xs text-gray-600 uppercase tracking-wide mb-1">Notizen</p>
                <p className="text-sm text-gray-700">{data.notes}</p>
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
};
